import { ChangeDetectorRef, Directive, Input, OnChanges, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';

/**
 * Renders its content only when a feature switch is on. Off, unknown or failed to load all mean
 * nothing is rendered (fail closed).
 *
 *   <div *appFeatureSwitch="'filters.actions'">...</div>
 *   <div *appFeatureSwitch="'filters.actions'; else legacy">...</div>
 *   <ng-template #legacy>...</ng-template>
 *
 * It asks again after every navigation, so content that outlives a page change (the nav menu) follows a switch
 * turned on or off without a reload. Until the new answer arrives it keeps showing the last one.
 */
@Directive({
  selector: '[appFeatureSwitch]',
  standalone: false
})
export class FeatureSwitchDirective implements OnChanges, OnDestroy {
  @Input() appFeatureSwitch: string;
  @Input() appFeatureSwitchElse: TemplateRef<any> | null = null;

  private subscription: Subscription | null = null;
  private navigationSubscription: Subscription | null = null;
  private enabled = false;
  /** The template currently rendered; undefined until the first render. */
  private rendered: TemplateRef<any> | null | undefined = undefined;

  constructor(
    private template: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private featureSwitches: FeatureSwitchService,
    private cdr: ChangeDetectorRef,
    router: Router
  ) {
    // Some specs stub the Router without events; the directive then simply never re-asks.
    this.navigationSubscription = router?.events
      ?.pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.ask()) ?? null;
  }

  ngOnChanges(): void {
    this.enabled = false;
    this.render();
    this.ask();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.navigationSubscription?.unsubscribe();
  }

  private ask(): void {
    this.subscription?.unsubscribe();
    this.subscription = this.featureSwitches.isEnabled(this.appFeatureSwitch).subscribe(enabled => {
      this.enabled = enabled;
      this.render();
    });
  }

  private render(): void {
    const template = this.enabled ? this.template : this.appFeatureSwitchElse;
    if (template === this.rendered) {
      return;
    }
    this.rendered = template;
    this.viewContainer.clear();
    if (template) {
      this.viewContainer.createEmbeddedView(template);
    }
    this.cdr.markForCheck();
  }
}
